import * as React from 'react';
import { FileRecord } from './components/FileRecord';
import { NewDirectoryDialog } from './components/NewDirectoryDialog';
import { DirectoryTable } from './components/DirectoryTable';
import Loading from '../misc/components/loading';
import { EditDirectoryDialog } from './components/EditDirectoryDialog';
import { useSecurityContext } from '../Core/SecurityContext';
import { MappedServerDirectory } from 'webportalclient';
import { Authorization } from '../misc/components/Authorization';
import { useCurrentStudyConfigurationContext } from '../Core/CurrentStudyConfigurationContext';
import { PermissionEnum } from '../misc/api/ApiPermissionEnum';
import { ODataParams, Query } from '../Components/Query';
import Collapsible from 'react-collapsible';
import { FileDownload } from '../misc/FileDownload';
import { useWebPortalApiClient, useWebPortalLegacyClient } from '../misc/api/WebPortalTokenClient';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MapToRoleOptions } from "../misc/OptionFormatters";

declare const rootUrl;

export interface MappedServerDirectoryCollapse extends MappedServerDirectory {
    IsOpen: boolean;
}

export const DirectoryVisualizer = () => {
    const client = useWebPortalApiClient();
    const { currentUserPermissions } = useSecurityContext();
    const { study } = useCurrentStudyConfigurationContext();
    const [directories, setDirectories] = React.useState<Array<MappedServerDirectoryCollapse>>([]);
    const [fetching, setFetching] = React.useState<boolean>(false);
    const [newDirectoryDialogVisible, setNewDirectoryDialogVisible] = React.useState<boolean>(false);
    const [isCheckingDirectory, setCheckingDirectory] = React.useState<boolean>(false);
    const [error, setError] = React.useState();
    const [editDirectoryDialogItem, setEditDirectoryDialogItem] = React.useState<string>();
    const { roles } = useSecurityContext();
    const legacyClient = useWebPortalLegacyClient();

    const assignableRoles = MapToRoleOptions(roles.filter(r => r.name !== "Device Hub Client" && r.name !== "Gateway Client"));

    const getFiles = async () => {
        setFetching(true);
        const directoriesResult = await client.client.getMappedServerDirectories(undefined, undefined, "name asc");

        setDirectories(directoriesResult.value.map(d => ({ ...d, IsOpen: false })));
        setFetching(false);
    }

    React.useEffect(() => {
        getFiles();
    }, []);

    const handleNewDirectoryClick = () => {
        setNewDirectoryDialogVisible(true);
    }

    const handleCollapseAllClick = () => {
        const isAnyOpen = directories.some(d => d.IsOpen);
        const newDirectories = directories.map(d => ({
            ...d,
            IsOpen: !isAnyOpen,
        }));
        setDirectories(newDirectories);
    };

    const handleToggle = (index: number) => {
        const newDirectories = [...directories];
        newDirectories[index].IsOpen = !newDirectories[index].IsOpen;
        setDirectories(newDirectories);
    };

    const checkDirectory = (form) => {
        setCheckingDirectory(true);

        try {
            return legacyClient.checkDirectory({
                ...form,
                UserRoles: form.UserRoles.map(r => ({ ...r, Name: r.label, Id: r.value })),
            })
                .then(json => {
                    setNewDirectoryDialogVisible(json.errorMessage ? true : false);
                    setCheckingDirectory(false);
                    setError(json.errorMessage);
                    getFiles();
                });
        } catch (ex) {
            setError(ex);
        }

    }

    const removeDirectoryMapping = (directoryName) => {
        return legacyClient.removeDirectory(directoryName)
            .then(json => getFiles());
    }

    const getFile = (filename) => {
        return legacyClient.downloadSharedFile(filename)
            .then(response => {
                const filename = response.headers.get('filename');
                response.blob().then(blob => {
                    FileDownload(blob, filename);
                });
            })
    }
    const handleRefresh = async (path: string, params: ODataParams) => {
        let filter = params.filter ? `(${params.filter}) and ` : "";
        filter += `directoryName eq '${path}'`;
        return await client.client.getDirectoryFiles(undefined, filter, params.orderby, undefined, params.top, params.skip);
    };



    const renderDirectories = () => {
        if (fetching) {
            return <Loading />;
        } else if (directories.length > 0) {

            const tables = directories.map((directory, i) => {
                const trigger =
                    <span>
                        <b>{directory.name}</b>
                        <Authorization currentPermissions={currentUserPermissions} studyId={study.id} permissionId={PermissionEnum.MapServerDirectories}>
                            <span className="pull-right">
                                <a className="btn btn-xs btn-info" onClick={() => setEditDirectoryDialogItem(directory.name)}>Edit</a>&nbsp;
                                <a className="btn btn-xs btn-danger" onClick={() => removeDirectoryMapping(directory.name)}><FontAwesomeIcon icon={faXmark} /> Remove</a>
                            </span>
                        </Authorization>
                    </span>;
                return (
                    <div key={i}>
                        <Collapsible trigger={trigger} open={directory.IsOpen} handleTriggerClick={() => handleToggle(i)}>
                            <Query onRefresh={(odataParams) => handleRefresh(directory.serverDirectory, odataParams)}>
                                <DirectoryTable
                                    directory={directory}
                                    roles={assignableRoles}
                                    handleFileDownload={(filename) => getFile(filename)}
                                />
                            </Query>
                        </Collapsible>

                    </div>
                );
            });

            return [tables];
        } else {
            return <div className="alert alert-info">There are no files that you have permission to access right now</div>;
        }
    }

    return (
        <>
            <div id="sharedFiles" key="titleRow" className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Shared Files</h2>
                </div>
                <Authorization currentPermissions={currentUserPermissions} studyId={study.id} permissionId={PermissionEnum.MapServerDirectories}>
                    <div className="col-md-6 h2">
                        <div className="pull-right">
                            <button className="btn btn-primary" onClick={handleNewDirectoryClick}>New Directory</button>
                            &nbsp;
                            <button className="btn btn-primary btn-outline" onClick={handleCollapseAllClick}>{directories.some(d => d.IsOpen) ? "Collapse All Folders" : "Expand All Folders"}</button>
                        </div>
                    </div>
                </Authorization>
            </div>
            <div key="mainContent" className="row">
                <div className="col-md-12">
                    {renderDirectories()}
                    {newDirectoryDialogVisible ? <NewDirectoryDialog
                        roles={assignableRoles}
                        checkingDirectory={isCheckingDirectory}
                        errorMessage={error}
                        handleLinkClick={(directory) => checkDirectory(directory)}
                        handleCloseDialog={() => setNewDirectoryDialogVisible(false)}
                    /> : null}
                    {editDirectoryDialogItem ? <EditDirectoryDialog
                        roles={assignableRoles}
                        item={directories.find(d => d.name === editDirectoryDialogItem)}
                        checkingDirectory={isCheckingDirectory}
                        errorMessage={error}
                        handleLinkClick={(directory) => { checkDirectory(directory); setEditDirectoryDialogItem(undefined); }}
                        handleCloseDialog={() => setEditDirectoryDialogItem(undefined)}
                    /> : null}
                </div>
            </div>
        </>
    );
}
